import { useEffect, useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import { GET_TREND } from "./TrendsApi";
import useRetry from "../../useRetry";

export interface UseTrendVariables {
  widgetKey?: string;
  selection?: string[];
  // Most widgets pass a single timescale string; FindingsByDataSource passes a
  // single-element array. Both shapes reach the server unchanged.
  timescale?: string | string[];
  group_by?: string[];
  aggregate_functions?: string[];
  round_functions?: string[];
  filters_config?: {
    filtersjson?: string | null;
    scopesjson?: string | null;
    scopesid?: string | null;
    filters_fields_to_exclude?: string[];
    filters_fields_to_override?: string[];
  };
  snapshots_filters_config?: {
    filtersjson?: string | null;
    scopesjson?: string | null;
    scopesid?: string | null;
    filters_fields_to_exclude?: string[];
    filters_fields_to_include?: string[];
  };
}

export interface UseTrendOptions {
  variables: UseTrendVariables;
  skip?: boolean;
}

interface TrendQueryData {
  trends: string;
}

export interface UseTrendReturn {
  // Parsed JSON from the `trends` string; shape varies per widget consumer.
  trendData: unknown;
  // Preserves original truthiness semantics: the error object when present and
  // not refetching, otherwise false. Consumers read it via `!!trendDataError`.
  trendDataError: unknown;
  trendLoading: boolean;
  refetchTrend: () => void;
}

const useTrend = ({
  variables,
  skip = false,
}: UseTrendOptions): UseTrendReturn => {
  const [trendData, setTrendData] = useState<unknown>(null);

  const { data, error, loading, refetch, networkStatus } =
    useQuery<TrendQueryData>(GET_TREND, {
      variables: { ...variables },
      skip: skip,
      notifyOnNetworkStatusChange: true,
    });

  const { finalError, resetRetries } = useRetry({
    requestError: error,
    refetch,
  });

  useEffect(() => {
    if (!!data && !loading) {
      resetRetries();
      let parsedData = JSON.parse(data?.trends);
      setTrendData(parsedData);
    }
  }, [data, loading]);

  return {
    trendData,
    trendDataError: finalError && networkStatus !== NetworkStatus.refetch,
    trendLoading: loading || networkStatus === NetworkStatus.refetch,
    refetchTrend: refetch,
  };
};

export default useTrend;
